import type {
  FormBuilderDraft,
  FormBuilderField,
} from "../../types/formBuilder";
import { resolveFieldType } from "../forms/normalizeFieldType";
import { createEmptyField, generateUniqueFieldName } from "./draft";
import { defaultPrivacyFieldLabel } from "../forms/privacyConsent";

const PERSONAL_DATA_TYPES = new Set(["fio", "email", "phone"]);

export function hasPrivacyField(draft: FormBuilderDraft): boolean {
  return draft.sections.some((section) =>
    section.fields.some((field) => resolveFieldType(field.type) === "privacy"),
  );
}

export function collectPersonalDataTypes(
  draft: FormBuilderDraft,
): Array<"fio" | "email" | "phone"> {
  const found = new Set<"fio" | "email" | "phone">();

  for (const section of draft.sections) {
    for (const field of section.fields) {
      const type = resolveFieldType(field.type);
      if (PERSONAL_DATA_TYPES.has(type)) {
        found.add(type as "fio" | "email" | "phone");
      }
    }
  }

  return [...found];
}

export function shouldSuggestPrivacyField(draft: FormBuilderDraft): boolean {
  if (hasPrivacyField(draft)) return false;
  return collectPersonalDataTypes(draft).length > 0;
}

export function createPrivacyField(
  order: number,
  existingNames: Iterable<string>,
): FormBuilderField {
  const used = new Set(existingNames);
  const name = generateUniqueFieldName(used, "privacy");

  return {
    ...createEmptyField(order, used),
    type: "privacy",
    name,
    label: defaultPrivacyFieldLabel(),
    required: true,
    nameManuallyEdited: true,
    presentation: "stacked",
  };
}

export function addPrivacyFieldToDraft(
  draft: FormBuilderDraft,
): FormBuilderDraft {
  const lastIndex = draft.sections.length - 1;
  const existingNames = draft.sections.flatMap((section) =>
    section.fields.map((field) => field.name),
  );
  const lastSection = draft.sections[lastIndex];
  const newField = createPrivacyField(lastSection.fields.length, existingNames);

  return {
    ...draft,
    sections: draft.sections.map((section, index) =>
      index === lastIndex
        ? {
            ...section,
            fields: [...section.fields, newField].map((field, fieldIndex) => ({
              ...field,
              order: fieldIndex,
            })),
          }
        : section,
    ),
  };
}

export function personalDataTypeLabel(type: "fio" | "email" | "phone"): string {
  switch (type) {
    case "fio":
      return "ФИО";
    case "email":
      return "Email";
    case "phone":
      return "Телефон";
  }
}
